import { Box } from "@mui/material";
import { DotLegalCheckbox, DotLegalDatePicker, DotLegalHeader, DotLegalSelect, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { useTranslation } from "../../../../localization/useTranslation.ts";
import { SelectableObligationTemplates } from "../../../../entityTemplates/EntityTemplate.types.ts";
import { SelectableItem } from "../../../../processingActivity/ProcessingActivity.types.ts";
import { SelectedObligationTemplate } from "../../../addContractButton/createContractPane/CreateContractPane.types.ts";
import { ObligationValidationErrors } from "../ObligationTemplatesList.types.ts";
import { UserSelectableItem } from "src/user/User.types.ts";

export interface ObligationTemplateRowProps {
    template: SelectableObligationTemplates;
    selectedObligation: SelectedObligationTemplate | undefined;
    isSelected: boolean;
    displayStartDate: Date | null;
    displayEndDate: Date | null;
    relativeStartDateDescription?: string;
    relativeEndDateDescription?: string;
    selectableUsers: Array<UserSelectableItem> | undefined;
    isLoadingUsers: boolean;
    onToggleSelection: () => void;
    onStartDateChange: (date: Date | null) => void;
    onEndDateChange: (date: Date | null) => void;
    onResponsibleChange: (responsibleId: string | null) => void;
    validationErrors?: ObligationValidationErrors;
}

function ObligationTemplateRow(props: ObligationTemplateRowProps) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const hasRelativeStartDate = props.template.startDate?.value != null;
    const hasRelativeEndDate = props.template.endDate?.value != null;

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", marginTop: -1.5 }}>
                <DotLegalCheckbox
                    checked={props.isSelected}
                    margin={{ marginLeft: -1.5 }}
                    onChange={props.onToggleSelection}
                    disabled={props.template.isMandatory && !props.template.isUsed}
                />
                <DotLegalHeader headerStyle="small" marginBottom={0}>
                    {props.template.name}
                </DotLegalHeader>
                {props.template.isMandatory && !props.template.isUsed && (
                    <DotLegalTooltip text={translateString("mandatoryObligationTemplateInfo")}>
                        <PriorityHighIcon
                            sx={(theme) => ({
                                fill: theme.palette.error.main,
                                width: 18,
                                height: 18,
                            })}
                        />
                    </DotLegalTooltip>
                )}
                {props.template.isUsed && (
                    <Box sx={(theme) => ({ marginLeft: 1, color: theme.palette.grey[500], fontSize: "0.875rem" })}>
                        ({translateString("obligationTemplateAlreadyAdded")})
                    </Box>
                )}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                <Box sx={{ "& .Mui-disabled .MuiSvgIcon-root": { opacity: 0.4 } }}>
                    <DotLegalTooltip text={hasRelativeStartDate ? translateString("relativeDateCalculatedInfo") : undefined}>
                        <span>
                            <DotLegalDatePicker
                                label={props.template.isCarriedOutOnce ? translateString("deadline") : translateString("startDate")}
                                value={props.displayStartDate}
                                onChange={props.onStartDateChange}
                                language={getDateTimePickerLanguage()}
                                noMargin
                                disabled={!props.isSelected || hasRelativeStartDate}
                                error={!!props.validationErrors?.startDate}
                                helperMessage={props.validationErrors?.startDate}
                            />
                        </span>
                    </DotLegalTooltip>
                    {props.relativeStartDateDescription && (
                        <Box sx={(theme) => ({ color: theme.palette.grey[600], fontSize: "0.75rem", marginTop: 0.5 })}>
                            {props.relativeStartDateDescription}
                        </Box>
                    )}
                </Box>
                <Box sx={{ "& .Mui-disabled .MuiSvgIcon-root": { opacity: 0.4 } }}>
                    {!props.template.isCarriedOutOnce && (
                        <>
                            <DotLegalTooltip text={hasRelativeEndDate ? translateString("relativeDateCalculatedInfo") : undefined}>
                                <span>
                                    <DotLegalDatePicker
                                        label={translateString("endDate")}
                                        value={props.displayEndDate}
                                        onChange={props.onEndDateChange}
                                        language={getDateTimePickerLanguage()}
                                        noMargin
                                        disabled={!props.isSelected || hasRelativeEndDate}
                                        error={!!props.validationErrors?.endDate}
                                        helperMessage={props.validationErrors?.endDate}
                                    />
                                </span>
                            </DotLegalTooltip>
                            {props.relativeEndDateDescription && (
                                <Box sx={(theme) => ({ color: theme.palette.grey[600], fontSize: "0.75rem", marginTop: 0.5 })}>
                                    {props.relativeEndDateDescription}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
                <Box>
                    <DotLegalSelect
                        label={translateString("responsible")}
                        options={props.selectableUsers}
                        selectedItem={props.selectedObligation?.responsibleId ?? null}
                        onChange={props.onResponsibleChange}
                        noOptionsLabel={translateString("noOptions")}
                        isLoading={props.isLoadingUsers}
                        noMargin
                        disabled={!props.isSelected}
                        placeholder={translateString("responsible")}
                        errorText={props.validationErrors?.responsible}
                    />
                </Box>
            </Box>
        </Box>
    );
}

export default ObligationTemplateRow;
